<!--
  @component ScopeSelector — the Deployments project-scope picker.
  tier:   deployments chrome (rendered in the SubNav `lead` slot)

  A chip + multi-select dropdown that drives ScopeIO: All Projects · one · many.
  Reads/writes scope directly (instant — no navigation). The "Remember this
  scope" pin stores a per-tenant default open state.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { initial, projectGradient } from "../../home/home-util.js";

  const identity = useIdentityIO();
  const scope = useScopeIO();

  let open = $state(false);
  let query = $state("");
  let root: HTMLElement | undefined = $state();

  const label = $derived.by(() => {
    if (scope.isAll) return "All Projects";
    if (scope.count === 0) return "Select projects";
    if (scope.count === 1) {
      const id = scope.projectIds[0];
      return identity.projects.find((p) => p.id === id)?.name ?? "1 project";
    }
    return `${scope.count} projects`;
  });

  const filtered = $derived(
    identity.projects.filter((p) =>
      p.name.toLowerCase().includes(query.trim().toLowerCase()),
    ),
  );

  // Is the current live scope the pinned default?
  const isRememberedEqual = $derived.by(() => {
    const r = scope.rememberedScope;
    if (!r) return false;
    if (r.kind === "all") return scope.isAll;
    if (scope.isAll) return false;
    const a = [...r.ids].sort().join(",");
    const b = [...scope.projectIds].sort().join(",");
    return a === b;
  });

  function toggleRemember(): void {
    if (isRememberedEqual) scope.forget();
    else scope.remember();
  }

  // Close on outside click.
  $effect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (root && !root.contains(e.target as Node)) open = false;
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  });
</script>

<div class="scope" bind:this={root}>
  <button
    type="button"
    class="chip"
    onclick={() => (open = !open)}
    aria-haspopup="menu"
    aria-expanded={open}
  >
    <span class="chip-dot" class:all={scope.isAll}></span>
    <span class="chip-label">{label}</span>
    <span class="caret" aria-hidden="true">▾</span>
  </button>

  {#if open}
    <div class="menu" role="menu">
      <button
        type="button"
        class="row all-row"
        class:active={scope.isAll}
        onclick={() => { scope.setAll(); open = false; }}
        role="menuitemradio"
        aria-checked={scope.isAll}
      >
        <span class="check">{scope.isAll ? "✓" : ""}</span>
        <span class="row-label">All Projects</span>
        <span class="row-count">{identity.projects.length}</span>
      </button>

      <div class="menu-sep"></div>

      <input
        class="search"
        type="text"
        placeholder="Filter projects…"
        bind:value={query}
      />

      <div class="list">
        {#each filtered as p (p.id)}
          <button
            type="button"
            class="row"
            class:active={scope.has(p.id)}
            onclick={() => scope.toggle(p.id)}
            role="menuitemcheckbox"
            aria-checked={scope.has(p.id)}
          >
            <span class="check">{scope.has(p.id) ? "✓" : ""}</span>
            <span class="mark" style="background: {projectGradient(p.id)}">{initial(p.name)}</span>
            <span class="row-label">{p.name}</span>
          </button>
        {:else}
          <p class="empty">No projects match.</p>
        {/each}
      </div>

      <div class="menu-sep"></div>
      <div class="foot">
        <button type="button" class="pin" class:on={isRememberedEqual} onclick={toggleRemember}>
          <span class="pin-icon" aria-hidden="true">{isRememberedEqual ? "★" : "☆"}</span>
          {isRememberedEqual ? "Default scope" : "Remember this scope"}
        </button>
        {#if !scope.isAll && scope.count > 0}
          <button type="button" class="clear" onclick={() => scope.clear()}>Clear</button>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .scope { position: relative; display: inline-flex; }
  .chip {
    display: inline-flex; align-items: center; gap: var(--space-2);
    background: var(--surface-sunken); border: 0.5px solid var(--border);
    border-radius: var(--radius-pill); padding: 5px 10px 5px 9px;
    color: var(--text-primary); font: inherit; font-size: 12.5px; font-weight: 500;
    cursor: pointer; white-space: nowrap;
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .chip:hover { background: var(--bg-hover); border-color: var(--border-strong); }
  .chip-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent, #5b8cff); }
  .chip-dot.all { background: var(--success); }
  .caret { font-size: 9px; opacity: 0.5; }

  .menu {
    position: absolute; top: calc(100% + 6px); left: 0; z-index: 200;
    width: 280px; max-height: 60vh; display: flex; flex-direction: column;
    background: var(--surface-raised); border: 0.5px solid var(--border);
    border-radius: var(--radius-md); box-shadow: var(--shadow-lg); padding: var(--space-1);
  }
  .row {
    display: flex; align-items: center; gap: var(--space-2);
    background: transparent; border: 0; border-radius: var(--radius-sm);
    padding: 7px var(--space-2); font: inherit; font-size: 13px;
    color: var(--text-secondary); cursor: pointer; text-align: left; width: 100%;
    transition: background var(--ease-fast), color var(--ease-fast);
  }
  .row:hover { background: var(--bg-hover); color: var(--text-primary); }
  .row.active { color: var(--text-primary); }
  .check { width: 14px; flex-shrink: 0; color: var(--success); font-size: 12px; }
  .row-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row-count { font-size: 11px; color: var(--text-tertiary); }
  .mark {
    width: 20px; height: 20px; border-radius: var(--radius-sm); flex-shrink: 0;
    display: grid; place-items: center; font-size: 10px; font-weight: 600;
    color: rgba(255,255,255,0.92);
  }
  .menu-sep { height: 0.5px; background: var(--border); margin: var(--space-1) var(--space-2); }
  .search {
    margin: 2px var(--space-1) var(--space-1); padding: 7px var(--space-2);
    background: var(--surface-sunken); border: 0.5px solid var(--border-input);
    border-radius: var(--radius-sm); color: var(--text-primary); font: inherit; font-size: 12.5px;
  }
  .search:focus { outline: none; border-color: var(--border-focus); }
  .list { overflow-y: auto; display: flex; flex-direction: column; scrollbar-width: thin; }
  .empty { margin: 0; padding: var(--space-3) var(--space-2); font-size: 12.5px; color: var(--text-tertiary); }
  .foot { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); padding: 2px; }
  .pin {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; border: 0; color: var(--text-tertiary);
    font: inherit; font-size: 12px; padding: 6px var(--space-2); cursor: pointer; border-radius: var(--radius-sm);
  }
  .pin:hover { background: var(--bg-hover); color: var(--text-secondary); }
  .pin.on { color: var(--warning, #e6b450); }
  .pin-icon { font-size: 13px; }
  .clear {
    background: transparent; border: 0; color: var(--text-tertiary);
    font: inherit; font-size: 12px; padding: 6px var(--space-2); cursor: pointer; border-radius: var(--radius-sm);
  }
  .clear:hover { background: var(--bg-hover); color: var(--text-secondary); }
</style>
